import { useCallback, useMemo } from 'react'

import { ArrowRightAlt } from '@mui/icons-material'
import {
  Box,
  Checkbox,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { IconArrowNarrowRight, IconEqual, IconExclamationCircleFilled } from '@tabler/icons-react'
import { useTranslate } from 'react-admin'
import { DateHelper } from '../../helpers/Date.helper'
import { NumberFormatter } from '../../helpers/NumberFormatter.helper'
import { ArtworkDissociateApiDTO, ChangeApiDTO, PeriodsItem } from '../../types/Artwork'

import styles from './ArtworksDissociation.module.scss'

type ArtworkDissociationDataProps = {
  data: ArtworkDissociateApiDTO
  selectedPeriods: PeriodsItem[]
  onSelect: (selectedPeriod: PeriodsItem) => void
}

export const ArtworkDissociationData = ({
  data,
  selectedPeriods,
  onSelect,
}: ArtworkDissociationDataProps) => {
  const translate = useTranslate()

  const periods = useMemo(
    () =>
      selectedPeriods.reduce(
        (obj, curr) => {
          obj.push(`${curr.year}-${curr.month.toString().padStart(2, '0')}`)

          return obj
        },
        [] as string[],
      ),
    [selectedPeriods],
  )

  const formatDataChange = useCallback((change: ChangeApiDTO) => {
    const changeAmount = parseFloat(change.beneficiaryAmountChangeInEuros)
    const newAmount = NumberFormatter.formatEuros(change.newBeneficiaryAmountInEuros)

    const format = {
      icon: <IconExclamationCircleFilled color="orange" />,
      amount: '',
      color: 'black',
      sign: '',
    }
    if (typeof changeAmount === 'number') {
      if (changeAmount === 0) {
        format.icon = <IconEqual size={20} color="#1976d2" />
        format.color = 'blue'
      }
      if (changeAmount > 0) {
        format.icon = (
          <IconArrowNarrowRight
            size={28}
            color="green"
            style={{
              transform: 'rotate(-45deg)',
            }}
          />
        )
        format.color = 'green'
        format.amount = change.beneficiaryAmountChangeInEuros
        format.sign = '+'
      }
      if (changeAmount < 0) {
        format.icon = (
          <IconArrowNarrowRight
            color="red"
            style={{
              transform: 'rotate(45deg)',
            }}
          />
        )
        format.color = 'red'
        format.amount = change.beneficiaryAmountChangeInEuros
      }
    }

    return (
      <Box display="flex" flexDirection="column" justifyItems="center" alignItems="center" gap={0}>
        <Typography className={styles.title}>{newAmount}</Typography>
        <Box display="flex" justifyItems="center" alignItems="center" gap={0}>
          {format.icon}
          {format.amount !== '' && (
            <Typography fontSize={12} color={format.color}>
              {format.sign}
              {NumberFormatter.formatEuros(format.amount)}
            </Typography>
          )}
        </Box>
      </Box>
    )
  }, [])

  return (
    <>
      <Grid container my={4} flexWrap="wrap" gap={3}>
        {data.previousBeneficiary.id !== data.newBeneficiary.id && (
          <Grid xs={12}>
            <Box component="b" display="block" mb={0.5}>
              {translate('_generics.beneficiary')}
            </Box>
            <Box display="flex" flexDirection="row" gap={4}>
              <Box display="flex" alignItems="baseline">
                <Typography width={400}>{data.previousBeneficiary.name}</Typography>
                <Chip
                  label={translate('_generics.source')}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Box>
              <ArrowRightAlt />
              <Box display="flex" alignItems="baseline" gap="1rem">
                <Chip
                  label={translate('_generics.destination')}
                  size="small"
                  color="success"
                  variant="outlined"
                />
                <Typography width={400}>{data.newBeneficiary.name}</Typography>
              </Box>
            </Box>
          </Grid>
        )}
        {data.previousProduct.id !== data.newProduct.id && (
          <Grid xs={12}>
            <Box component="b" display="block" mb={0.5}>
              {translate('_generics.product')}
            </Box>
            <Box display="flex" flexDirection="row" gap={4}>
              <Box display="flex" alignItems="baseline">
                <Typography width={400}>{data.previousProduct.name}</Typography>
                <Chip
                  label={translate('_generics.source')}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Box>
              <ArrowRightAlt />
              <Box display="flex" alignItems="baseline" gap="1rem">
                <Chip
                  label={translate('_generics.destination')}
                  size="small"
                  color="success"
                  variant="outlined"
                />
                <Typography width={400}>{data.newProduct.name}</Typography>
              </Box>
            </Box>
          </Grid>
        )}
        <Grid xs={12}>
          <Box component="b" display="block" mb={0.5}>
            {translate('_generics.artwork')}
          </Box>
          <Box display="flex" flexDirection="row" gap={4}>
            <Box display="flex" alignItems="baseline">
              <Typography width={400}>{data.previousArtwork.name}</Typography>
              <Chip label={translate('_generics.source')} size="small" color="warning" />
            </Box>
            <ArrowRightAlt />
            <Box display="flex" alignItems="baseline" gap="1rem">
              <Chip label={translate('_generics.destination')} size="small" color="success" />
              <Typography width={400}>{data.newArtwork.name}</Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
      {Object.keys(data.changes).length > 0 ? (
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>{''}</TableCell>
                {data.previousBeneficiary.id !== data.newBeneficiary.id ? (
                  <>
                    <TableCell align="right">
                      <Stack direction="column" alignItems="center">
                        <Box>{translate('_generics.beneficiary')}</Box>
                        <Chip
                          label={translate('_generics.source')}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="column" alignItems="center">
                        <Box>{translate('_generics.beneficiary')}</Box>
                        <Chip
                          label={translate('_generics.destination')}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                  </>
                ) : (
                  <TableCell align="right">
                    <Stack direction="column" alignItems="center">
                      <Box>{translate('_generics.beneficiary')}</Box>
                    </Stack>
                  </TableCell>
                )}
                {data.previousProduct.id !== data.newProduct.id ? (
                  <>
                    <TableCell align="right">
                      <Stack direction="column" alignItems="center">
                        <Box>{translate('_generics.product')}</Box>
                        <Chip
                          label={translate('_generics.source')}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="column" alignItems="center">
                        <Box>{translate('_generics.product')}</Box>
                        <Chip
                          label={translate('_generics.destination')}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                  </>
                ) : (
                  <TableCell align="right">
                    <Stack direction="column" alignItems="center">
                      <Box>{translate('_generics.product')}</Box>
                    </Stack>
                  </TableCell>
                )}
                <TableCell align="right">
                  <Stack direction="column" alignItems="center">
                    <Box>{translate('_generics.artwork')}</Box>
                    <Chip label={translate('_generics.source')} size="small" color="warning" />
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="column" alignItems="center">
                    <Box>{translate('_generics.artwork')}</Box>
                    <Chip label={translate('_generics.destination')} size="small" color="success" />
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(data.changes).map(([period, ad]: [string, any]) => (
                <TableRow
                  className={!periods.includes(period) ? styles.disabledRow : ''}
                  key={period}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell>
                    <Checkbox
                      checked={periods.includes(period)}
                      onChange={(_, checked) =>
                        onSelect({
                          month: ad.periodMonth,
                          year: ad.periodYear,
                        })
                      }
                    />
                    <b className={styles.title}>{DateHelper.format(period, 'MMM YYYY')}</b>
                  </TableCell>
                  {ad.previousBeneficiaryChange && (
                    <TableCell align="right">
                      {formatDataChange(ad.previousBeneficiaryChange)}
                    </TableCell>
                  )}
                  {ad.newBeneficiaryChange && (
                    <TableCell align="right">{formatDataChange(ad.newBeneficiaryChange)}</TableCell>
                  )}
                  {ad.previousProductChange && (
                    <TableCell align="right">
                      {formatDataChange(ad.previousProductChange)}
                    </TableCell>
                  )}
                  {ad.newProductChange && (
                    <TableCell align="right">{formatDataChange(ad.newProductChange)}</TableCell>
                  )}
                  <TableCell align="right">{formatDataChange(ad.previousArtworkChange)}</TableCell>
                  <TableCell align="right">{formatDataChange(ad.newArtworkChange)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box display="flex" justifyContent="center">
          <Typography component="i">
            {translate('pages.artworks.dissociateArtworks.noFinancialDataToDissociate')}
          </Typography>
        </Box>
      )}
    </>
  )
}
